import type { DeepResearchKnowledgeGraphMemory, DeepResearchSource } from './types';

const CAPITALIZED_ENTITY_PATTERN = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})\b/g;
const DATE_PATTERN = /\b(?:19|20)\d{2}\b/g;

const unique = <T,>(items: T[]) => Array.from(new Set(items));

export function createEmptyKnowledgeGraphMemory(): DeepResearchKnowledgeGraphMemory {
  return {
    entities: [],
    claims: [],
    events: [],
    codeReferences: [],
    unresolvedQuestions: [],
    sourceLinks: [],
  };
}

export function updateKnowledgeGraphMemory(params: {
  memory: DeepResearchKnowledgeGraphMemory;
  content: string;
  sources?: DeepResearchSource[];
}) {
  const { memory, content, sources = [] } = params;
  const lines = content
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const entities = unique(Array.from(content.matchAll(CAPITALIZED_ENTITY_PATTERN)).map((match) => match[1]).slice(0, 20));
  const events = unique(Array.from(content.matchAll(DATE_PATTERN)).map((match) => match[0]).slice(0, 12));
  const claims = unique(lines.filter((line) => /\[S\d+\]/.test(line) || line.startsWith('- ') || /^\d+\./.test(line)).slice(0, 20));
  const unresolvedQuestions = unique(lines.filter((line) => line.includes('?')).slice(0, 10));
  const codeReferences = unique(lines.filter((line) => /src\/|\.tsx|\.ts|function|component/i.test(line)).slice(0, 10));
  const sourceLinks = unique(
    sources.map((source) => `${source.id}::${source.title}`).slice(0, 30)
  ).map((item) => {
    const [sourceId, title] = item.split('::');
    return { sourceId, title };
  });

  return {
    entities: unique([...memory.entities, ...entities]).slice(0, 40),
    claims: unique([...memory.claims, ...claims]).slice(0, 40),
    events: unique([...memory.events, ...events]).slice(0, 20),
    codeReferences: unique([...memory.codeReferences, ...codeReferences]).slice(0, 20),
    unresolvedQuestions: unique([...memory.unresolvedQuestions, ...unresolvedQuestions]).slice(0, 20),
    sourceLinks: unique([
      ...memory.sourceLinks.map((item) => `${item.sourceId}::${item.title}`),
      ...sourceLinks.map((item) => `${item.sourceId}::${item.title}`),
    ]).map((item) => {
      const [sourceId, title] = item.split('::');
      return { sourceId, title };
    }),
  };
}
